import { MONTH_ABBREVIATIONS } from "../constants/month-name-abbreviations";
import { QUARTER_LABELS } from "../constants/quarter-labels";
import { TimePeriodAggregationType } from "../constants/time-period-aggregation-type";
import { WeekColumnNamePipe } from "../pipes/week-column-name";

// Extra space reserved per column for the sort icon, cell padding and borders.
const DEFAULT_COLUMN_HEADER_PADDING = 56;
const DEFAULT_COLUMN_HEADER_FONT = '600 14px "Segoe UI", Roboto, Arial, sans-serif';

let measurementCanvasContext: CanvasRenderingContext2D | null | undefined;

/**
 * Returns a column width that is guaranteed to be at least wide enough to
 * fit the header text without truncation. Kendo's autoFitColumns() does not
 * reliably account for headers rendered via kendoGridHeaderTemplate, so the
 * required minimum width is measured manually and used as a floor here.
 */
export function getColumnWidth(title: string, baseWidth: number = 100, padding: number = DEFAULT_COLUMN_HEADER_PADDING): number {
    const minWidth = Math.ceil(measureTextWidth(title)) + padding;
    return Math.max(baseWidth, minWidth);
}

export function measureTextWidth(text: string, font: string = DEFAULT_COLUMN_HEADER_FONT): number {
    if (!text) {
        return 0;
    }

    if (measurementCanvasContext === undefined) {
        const canvas = document.createElement('canvas');
        measurementCanvasContext = canvas.getContext('2d');
    }

    if (!measurementCanvasContext) {
        return text.length * 8;
    }

    measurementCanvasContext.font = font;
    return measurementCanvasContext.measureText(text).width;
}

export function buildTimePeriodColumns(
    rawData: any[],
    timePeriodType: string
): Array<{ field: string; display: string }> {

    let columns: Array<{ field: string; display: string }> = [];

    if (timePeriodType === TimePeriodAggregationType.Monthly) {

        const uniqueYearMonths = Array.from(
            new Set(rawData.map(r => `${r.year}-${r.month}`))
        ).sort((a, b) => {
            const [yA, mA] = a.split("-").map(Number);
            const [yB, mB] = b.split("-").map(Number);
            return yA === yB ? mA - mB : yA - yB;
        });

        columns = uniqueYearMonths.map(ym => {
            const [year, month] = ym.split("-").map(Number);
            const display = `${MONTH_ABBREVIATIONS[month]} ${year % 100}`;
            const field = `y${year % 100}_m${month.toString().padStart(2, '0')}`;
            return { field, display };
        });
    }

    else if (timePeriodType === TimePeriodAggregationType.Quarterly) {

        const uniqueYearQuarters = Array.from(
            new Set(rawData.map(r => `${r.year}-${r.quarter}`))
        ).sort((a, b) => {
            const [yA, qA] = a.split("-").map(Number);
            const [yB, qB] = b.split("-").map(Number);
            return yA === yB ? qA - qB : yA - yB;
        });

        columns = uniqueYearQuarters.map(yq => {
            const [year, quarter] = yq.split("-").map(Number);
            const display = `${QUARTER_LABELS[quarter]} ${year % 100}`;
            const field = `y${year % 100}_q${quarter}`;
            return { field, display };
        });
    }

    else if (timePeriodType === TimePeriodAggregationType.Yearly) {

        const uniqueYears = Array.from(
            new Set(rawData.map(r => r.year))
        ).sort((a, b) => (a as number) - (b as number));

        columns = uniqueYears.map(year => {
            return {
                field: `y${year}`,
                display: `${year}`
            };
        });
    }

    else if (timePeriodType === TimePeriodAggregationType.Weekly) {

        const uniqueWeeks = Array.from(
            new Set(rawData.map(r => `${r.year}-${r.week}`))
        ).sort((a, b) => {
            const [yA, wA] = a.split("-").map(Number);
            const [yB, wB] = b.split("-").map(Number);
            return yA === yB ? wA - wB : yA - yB;
        });

        const weekColumnNamePipe = new WeekColumnNamePipe();
        columns = uniqueWeeks.map(yw => {
            const [year, week] = yw.split("-").map(Number);
            const display = weekColumnNamePipe.transform({ year, week });
            const field = `y${year % 100}_w${week}`;
            return { field, display };
        });
    }

    return columns;
}
